import ElectronicAddressBadge from '@/components/invoices/ElectronicAddressBadge'
import InvoiceStateBadge from '@/components/invoices/InvoiceStateBadge'
import MonthNavigation from '@/components/recaps/MonthNavigation'
import { invoicingSkipReasonLabels } from '@/constants'
import {
  AwaitingDeposit,
  Contract,
  Invoice,
  InvoiceDeposit,
  InvoicedDeposit,
  InvoicingSkipReason,
  SharedProps,
  SkippedDeposit,
} from '@/types'
import { formatCurrency, formatVatRate } from '@/utils/format'
import { invoiceTotals } from '@/utils/invoices'
import { Head, Link, router, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Menu,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { IconAlertTriangle, IconChevronLeft, IconDots, IconSend } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useState } from 'react'

type ContractWithReadiness = Contract & { e_invoicing_missing_fields: string[] }

const skipReasons = Object.keys(invoicingSkipReasonLabels) as InvoicingSkipReason[]

function SkipReasonItem({ serviceId, reason }: { serviceId: number; reason: InvoicingSkipReason }) {
  const { current_organization } = usePage<SharedProps>().props

  const handleSkip = () =>
    router.post(
      `/organizations/${current_organization?.slug}/skipped_deposits`,
      { service_id: serviceId, reason },
      { preserveScroll: true },
    )

  return <Menu.Item onClick={handleSkip}>{invoicingSkipReasonLabels[reason]}</Menu.Item>
}

function DepositRow({
  deposit,
  checked,
  onToggle,
}: {
  deposit: InvoiceDeposit
  checked: boolean
  onToggle: (serviceId: number) => void
}) {
  const { current_organization } = usePage<SharedProps>().props
  const handleToggle = () => onToggle(deposit.service_id)
  // Outside the franchise a 0 % rate is refused by the platform: the invoice won't go out with this deposit.
  const untaxed = Number(deposit.vat_rate) === 0 && current_organization?.vat_regime !== 'franchise'

  return (
    <Table.Tr>
      <Table.Td>
        <Checkbox checked={checked} onChange={handleToggle} aria-label={`Facturer ${deposit.label}`} />
      </Table.Td>
      <Table.Td>#{deposit.order_id}</Table.Td>
      <Table.Td>{dayjs(deposit.deposited_on).format('DD/MM/YYYY')}</Table.Td>
      <Table.Td>
        {deposit.completed_on ? (
          dayjs(deposit.completed_on).format('DD/MM/YYYY')
        ) : (
          <Badge size="xs" variant="light" color="blue">
            En cours
          </Badge>
        )}
      </Table.Td>
      <Table.Td>{deposit.label}</Table.Td>
      <Table.Td>{deposit.reference}</Table.Td>
      <Table.Td ta="right">{formatCurrency(deposit.price)}</Table.Td>
      <Table.Td ta="right" c={untaxed ? 'red.7' : undefined} fw={untaxed ? 600 : undefined}>
        {formatVatRate(deposit.vat_rate)}
      </Table.Td>
      <Table.Td>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" aria-label={`Ne pas facturer ${deposit.label}`}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Ne pas facturer</Menu.Label>
            {skipReasons.map((reason) => (
              <SkipReasonItem key={reason} serviceId={deposit.service_id} reason={reason} />
            ))}
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  )
}

function SkippedDepositLine({ deposit }: { deposit: SkippedDeposit }) {
  const { current_organization } = usePage<SharedProps>().props

  const handleResume = () =>
    router.delete(`/organizations/${current_organization?.slug}/skipped_deposits/${deposit.service_id}`, {
      preserveScroll: true,
    })

  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Text size="sm" c="dimmed">
        #{deposit.order_id} · {deposit.label} · {formatCurrency(deposit.price)} —{' '}
        {invoicingSkipReasonLabels[deposit.reason]}
      </Text>
      <Button variant="subtle" size="compact-xs" onClick={handleResume}>
        Remettre à facturer
      </Button>
    </Group>
  )
}

function BlockerAlert({ blocker, contract }: { blocker: string; contract: Contract }) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization?.slug}`
  const fixesContract = blocker.startsWith('Informations manquantes') || blocker.startsWith("L'adresse")
  const fixPath = fixesContract ? `${basePath}/contracts/${contract.id}/edit` : `${basePath}/e_invoicing_setting`

  return (
    <Alert variant="light" color="red" icon={<IconAlertTriangle />} title="Facture impossible pour l'instant">
      {blocker}.{' '}
      <Anchor component={Link} href={fixPath}>
        {fixesContract ? 'Compléter le contrat' : 'Ouvrir Facture électronique'}
      </Anchor>
    </Alert>
  )
}

export default function InvoicesNew({
  contract,
  month,
  deposits,
  invoiced_deposits,
  skipped_deposits,
  awaiting_completion,
  blocker,
  invoices,
}: {
  contract: ContractWithReadiness
  month: string
  deposits: InvoiceDeposit[]
  invoiced_deposits: InvoicedDeposit[]
  skipped_deposits: SkippedDeposit[]
  awaiting_completion: AwaitingDeposit[]
  blocker: string | null
  invoices: Invoice[]
}) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization?.slug}`
  const [selectedIds, setSelectedIds] = useState<number[]>(deposits.map((deposit) => deposit.service_id))
  const [submitting, setSubmitting] = useState(false)

  const selected = deposits.filter((deposit) => selectedIds.includes(deposit.service_id))
  const totals = invoiceTotals(selected)
  const allSelected = selected.length === deposits.length && deposits.length > 0
  const billingAddress = [contract.street_address, [contract.postal_code, contract.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')

  const basisCaption =
    current_organization?.invoicing_basis === 'completion' ? 'Prestations réalisées en' : 'Prestations déposées en'

  const handleBack = () => router.get(`${basePath}/recaps/${contract.id}`, { month })

  const handleMonthChange = (value: string) =>
    router.get(`${basePath}/invoices/new`, { contract_id: contract.id, month: value })

  const handleToggle = (serviceId: number) =>
    setSelectedIds((current) =>
      current.includes(serviceId) ? current.filter((id) => id !== serviceId) : [...current, serviceId],
    )

  const handleToggleAll = () => setSelectedIds(allSelected ? [] : deposits.map((deposit) => deposit.service_id))

  const handleFinish = () => setSubmitting(false)

  const handleSubmit = () => {
    const message =
      `Émettre la facture de ${formatCurrency(totals.includingTax)} TTC pour ${contract.customer_name} ? ` +
      'Elle sera transmise à sa plateforme de réception et ne pourra plus être supprimée.'
    if (!confirm(message)) return
    setSubmitting(true)
    router.post(
      `${basePath}/invoices`,
      { contract_id: contract.id, month, service_ids: selectedIds },
      { onFinish: handleFinish },
    )
  }

  return (
    <>
      <Head title={`Nouvelle facture - ${current_organization?.name}`} />

      <Stack gap="md">
        <UnstyledButton onClick={handleBack} w="fit-content">
          <Group gap={4} c="dimmed">
            <IconChevronLeft size={18} />
            <Text size="sm">Récapitulatif {contract.customer_name}</Text>
          </Group>
        </UnstyledButton>

        <Title order={3}>{`Nouvelle facture : ${contract.customer_name} — ${dayjs(`${month}-01`).format('MMMM YYYY')}`}</Title>

        <Text size="sm" c="dimmed" mt={-8}>
          {`${basisCaption} ${dayjs(`${month}-01`).format('MMMM YYYY')} · `}
          <Anchor component={Link} href={`${basePath}/e_invoicing_setting`} size="sm">
            Changer le mois de facturation
          </Anchor>
        </Text>

        <MonthNavigation month={month} onChange={handleMonthChange} />

        {blocker && <BlockerAlert blocker={blocker} contract={contract} />}

        {invoices.length > 0 && (
          <Paper withBorder radius="md" p="md">
            <Text size="sm" fw={600} mb={6}>
              Déjà facturé pour ce mois
            </Text>
            <Stack gap={4}>
              {invoices.map((invoice) => (
                <Group key={invoice.id} gap="sm">
                  <Anchor component={Link} href={`${basePath}/invoices/${invoice.id}`} size="sm">
                    {invoice.number}
                  </Anchor>
                  <Text size="sm">{formatCurrency(invoice.total_including_tax)}</Text>
                  <InvoiceStateBadge state={invoice.state} size="sm" />
                </Group>
              ))}
            </Stack>
          </Paper>
        )}

        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Client facturé
              </Text>
              <Text fw={600}>{contract.legal_name || contract.customer_name}</Text>
              <Text size="sm">{billingAddress || '—'}</Text>
              <Text size="sm" c="dimmed">
                SIREN {contract.siren ?? '—'}
                {contract.vat_number ? ` · TVA ${contract.vat_number}` : ''}
              </Text>
            </Stack>
            <Stack gap={4} align="flex-end">
              <Text size="sm">{contract.electronic_address ?? 'Aucune adresse électronique'}</Text>
              {contract.electronic_address && <ElectronicAddressBadge status={contract.electronic_address_status} />}
            </Stack>
          </Group>
        </Paper>

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={800}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selected.length > 0 && !allSelected}
                      onChange={handleToggleAll}
                      aria-label="Tout sélectionner"
                    />
                  </Table.Th>
                  <Table.Th>Ticket</Table.Th>
                  <Table.Th>Dépôt</Table.Th>
                  <Table.Th>Réalisation</Table.Th>
                  <Table.Th>Prestation</Table.Th>
                  <Table.Th>Référence</Table.Th>
                  <Table.Th ta="right">Prix HT</Table.Th>
                  <Table.Th ta="right">TVA</Table.Th>
                  <Table.Th w={40} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {deposits.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={9}>
                      <Text c="dimmed" ta="center" py="md">
                        Aucune prestation à facturer pour ce mois
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {deposits.map((deposit) => (
                  <DepositRow
                    key={deposit.service_id}
                    deposit={deposit}
                    checked={selectedIds.includes(deposit.service_id)}
                    onToggle={handleToggle}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>

        {invoiced_deposits.length > 0 && (
          <Paper withBorder radius="md" p="md">
            <Text size="sm" fw={600} mb={6}>
              Dépôts du mois déjà facturés ({invoiced_deposits.length})
            </Text>
            <Stack gap={2}>
              {invoiced_deposits.map((deposit) => (
                <Text key={deposit.service_id} size="sm" c="dimmed">
                  {deposit.order_id ? `#${deposit.order_id} · ` : ''}
                  {deposit.label} · {formatCurrency(deposit.price)} —{' '}
                  <Anchor component={Link} href={`${basePath}/invoices/${deposit.invoice_id}`} size="sm">
                    {deposit.invoice_number}
                  </Anchor>
                </Text>
              ))}
            </Stack>
          </Paper>
        )}

        {skipped_deposits.length > 0 && (
          <Paper withBorder radius="md" p="md">
            <Text size="sm" fw={600} mb={6}>
              Dépôts du mois à ne pas facturer ({skipped_deposits.length})
            </Text>
            <Stack gap={2}>
              {skipped_deposits.map((deposit) => (
                <SkippedDepositLine key={deposit.service_id} deposit={deposit} />
              ))}
            </Stack>
          </Paper>
        )}

        {awaiting_completion.length > 0 && (
          <Paper withBorder radius="md" p="md">
            <Text size="sm" fw={600}>
              Pas encore réalisées ({awaiting_completion.length})
            </Text>
            <Text size="xs" c="dimmed" mb={6}>
              Elles seront facturées le mois où l'atelier les termine.
            </Text>
            <Stack gap={2}>
              {awaiting_completion.map((deposit) => (
                <Text key={deposit.service_id} size="sm" c="dimmed">
                  {`#${deposit.order_id} · déposée le ${dayjs(deposit.deposited_on).format('DD/MM/YYYY')} · ${deposit.label} · ${formatCurrency(deposit.price)}`}
                </Text>
              ))}
            </Stack>
          </Paper>
        )}

        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" align="center">
            <SimpleGrid cols={3} spacing="xl">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Total HT
                </Text>
                <Text fw={600}>{formatCurrency(totals.excludingTax)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  TVA
                </Text>
                <Text fw={600}>{formatCurrency(totals.tax)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Total TTC
                </Text>
                <Text fw={700}>{formatCurrency(totals.includingTax)}</Text>
              </div>
            </SimpleGrid>
            <Button
              size="md"
              leftSection={<IconSend size={18} />}
              onClick={handleSubmit}
              loading={submitting}
              disabled={!!blocker || selected.length === 0}
            >
              Valider et envoyer
            </Button>
          </Group>
        </Paper>
      </Stack>
    </>
  )
}
